import React, { useState, useEffect, useMemo, useRef } from 'react';
import GlassCard from '../components/ui/Card';
import GlassButton from '../components/ui/Button';
import { EditIcon, DeleteIcon, CloseIcon, ProjectIcon, SaveIcon } from '../components/ui/Icons';
import useData from '../hooks/useData';
import { getClientStatusPillClass, getQuoteStatusPillClass } from '../utils/helpers';
import useClickOutside from '../hooks/useClickOutside';

const StatusSelectorDropdown = ({ currentStatus, onSave }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    useClickOutside(dropdownRef, () => setIsOpen(false));
    
    const statusOptions = [
        { value: 'Ativo', label: 'Ativo' },
        { value: 'Inativo', label: 'Inativo' },
        { value: 'Potencial', label: 'Potencial' },
    ];

    const handleSelect = (status) => {
        onSave(status);
        setIsOpen(false);
    };

    return (
        <div ref={dropdownRef} className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`px-2 py-1 rounded-full text-sm flex items-center gap-2 transition-colors hover:bg-white/20 ${getClientStatusPillClass(currentStatus)}`}
            >
                <span>{currentStatus}</span>
                <EditIcon size={14}/>
            </button>
            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-36 bg-gray-800/90 backdrop-blur-lg border border-white/10 rounded-lg shadow-xl p-1 z-20 animate-fade-in">
                    <div className="space-y-1">
                        {statusOptions.map(option => (
                            <button
                                key={option.value}
                                onClick={() => handleSelect(option.value)}
                                className="w-full flex items-center gap-2 text-left px-3 py-1.5 rounded-md text-sm text-gray-200 transition-colors hover:bg-white/10 whitespace-nowrap"
                            >
                                <div className={`w-2 h-2 rounded-full ${getClientStatusPillClass(option.value).replace('bg-opacity-50', '')}`}></div>
                                <span>{option.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- INÍCIO DA ALTERAÇÃO 1: Adicionando `onQuoteClick` como prop ---
const ClientDetails = ({ client, onBack, onSave, onDelete, onProjectClick, onQuoteClick }) => {
// --- FIM DA ALTERAÇÃO 1 ---
    const [isEditing, setIsEditing] = useState(false);
    const [editedClient, setEditedClient] = useState(client);
    const { projects, tasks, quotes, currentUserProfile } = useData();

    useEffect(() => {
        setEditedClient(client);
    }, [client]);

    const clientProjects = useMemo(() => {
        return projects.filter(p => p.clientId === client.id);
    }, [client.id, projects]);
    
    const hourlyRate = useMemo(() => {
        if (!currentUserProfile || !currentUserProfile.workHoursPerMonth) return 0;
        const { monthlySalary = 4000, workHoursPerMonth = 160, costFactor = 1.5 } = currentUserProfile;
        return (monthlySalary * costFactor) / workHoursPerMonth;
    }, [currentUserProfile]);

    const calculateTotal = (items) => {
        if (!items || hourlyRate <= 0) return 0;
        return items.reduce((acc, item) => acc + (item.hours || 0) * (item.qty || 1) * hourlyRate, 0);
    };

    const clientQuotes = useMemo(() => {
        return quotes
            .filter(q => q.clientId === client.id)
            .sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
    }, [client.id, quotes]);
    
    const handleSave = () => {
        onSave(editedClient);
        setIsEditing(false);
    };
    
    const handleStatusSave = (newStatus) => {
        onSave({ ...client, status: newStatus });
    };

    const handleCancel = () => {
        setEditedClient(client);
        setIsEditing(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedClient(prev => ({...prev, [name]: value}));
    };

    if (!client) return null;

    return (
        <div className="p-4 space-y-6">
            <div className="flex justify-between items-center mb-4">
                {isEditing ? (
                    <input type="text" name="name" value={editedClient.name} onChange={handleChange} className="text-3xl font-bold text-white bg-white/20 p-2 rounded-lg w-full mr-4"/>
                ) : (
                    <div className="flex items-center gap-4">
                        <h2 className="text-3xl font-bold text-white">{client.name}</h2>
                        <StatusSelectorDropdown currentStatus={client.status} onSave={handleStatusSave} />
                    </div>
                )}
                <div className="flex items-center space-x-2">
                    {isEditing ? (
                        <>
                            <GlassButton onClick={handleSave} className="!bg-green-500/50 hover:!bg-green-500/60"><SaveIcon size={20} /></GlassButton>
                            <GlassButton onClick={handleCancel} className="!bg-red-500/30 hover:!bg-red-500/40"><CloseIcon size={20} /></GlassButton>
                        </>
                    ) : (
                        <>
                            <GlassButton onClick={() => setIsEditing(true)}><EditIcon size={20} /></GlassButton>
                            <GlassButton onClick={() => onDelete(client)} className="!bg-red-500/30 hover:!bg-red-500/40"><DeleteIcon size={20} /></GlassButton>
                            <GlassButton onClick={onBack}><CloseIcon size={20} /></GlassButton>
                        </>
                    )}
                </div>
            </div>
            <GlassCard>
                <h3 className="font-bold text-lg mb-2">Informações de Contato</h3>
                {isEditing ? (
                    <input type="text" name="contact" value={editedClient.contact} onChange={handleChange} className="text-gray-300 bg-white/20 p-2 rounded-lg w-full"/>
                ) : (
                    <p className="text-gray-300">{client.contact || 'Nenhum contato adicionado.'}</p>
                )}
            </GlassCard>
            <GlassCard>
                <h3 className="font-bold text-lg mb-2">Projetos Associados ({clientProjects.length})</h3>
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                    {clientProjects.length > 0 ? clientProjects.map(project => {
                        const projectTasks = tasks.filter(t => t.projectId === project.id);
                        const completedTasks = projectTasks.filter(t => t.status === 'Concluído');
                        const progress = projectTasks.length > 0 ? (completedTasks.length / projectTasks.length) * 100 : 0;
                        
                        return (
                            <div key={project.id} onClick={() => onProjectClick(project)} className="bg-white/5 p-3 rounded-lg cursor-pointer hover:bg-white/10">
                                <p className="font-semibold text-white">{project.name}</p>
                                <div className="flex justify-between items-center text-xs text-gray-300 mt-2">
                                    <span>Progresso</span>
                                    <span>{completedTasks.length} / {projectTasks.length}</span>
                                </div>
                                <div className="w-full bg-black/30 rounded-full h-1.5 mt-1">
                                    <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${progress}%` }}></div>
                                </div>
                            </div>
                        )
                    }) : (
                        <p className="text-gray-400 text-center py-4">Nenhum projeto encontrado para este cliente.</p>
                    )}
                </div>
            </GlassCard>

            {clientQuotes.length > 0 && (
                <GlassCard>
                    <h3 className="font-bold text-lg mb-4">Orçamentos Associados ({clientQuotes.length})</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                        {clientQuotes.map(quote => {
                            const total = calculateTotal(quote.items);
                            return (
                                // --- INÍCIO DA ALTERAÇÃO 2: Tornando o item do orçamento clicável ---
                                <button 
                                    key={quote.id} 
                                    onClick={() => onQuoteClick(quote)}
                                    className="w-full text-left bg-white/5 p-3 rounded-lg flex justify-between items-center transition-colors hover:bg-white/10"
                                >
                                    <div>
                                        <p className="font-semibold text-white">{quote.projectName}</p>
                                        <p className="text-xs text-gray-400">{quote.quoteNumber}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className={`px-2 py-1 rounded-full text-xs ${getQuoteStatusPillClass(quote.status)}`}>
                                            {quote.status}
                                        </span>
                                        <p className="font-bold text-lg text-emerald-400 w-32 text-right">
                                            {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </p>
                                    </div>
                                </button>
                                // --- FIM DA ALTERAÇÃO 2 ---
                            );
                        })}
                    </div>
                </GlassCard>
            )}
        </div>
    );
};

export default ClientDetails;